import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Plug,
  GitCompareArrows,
  Send,
  BarChart3,
  ArrowRight,
  FileCheck2,
  Boxes,
  Database,
  PhoneCall,
  ShoppingBasket,
  PackageCheck,
} from "lucide-react";
import { SERVICES } from "@/constants/testIds";

const steps = [
  {
    icon: Plug,
    label: "Step 01",
    title: "Connect your PMR",
    body:
      "Cascade Plus integrates with approved PMRs - ProScript Connect, Positive Solutions, RxWeb and Apotec. Our team handles setup end-to-end.",
    bullets: ["Approved PMR compatibility", "Setup in under 30 minutes", "Zero downtime to your branch"],
    visual: "connect",
  },
  {
    icon: GitCompareArrows,
    label: "Step 02",
    title: "Live supplier comparison",
    body:
      "Every line in your basket is priced in real time across AAH Pharmaceuticals, Alliance Healthcare, Phoenix, Ethigen, OTC Direct, Trident, B&S Distribution and Lexon, including your branded and UK deals.",
    bullets: ["Approved supplier list supported", "Brand & UK deals managed", "Maximises PI purchases automatically"],
    visual: "compare",
  },
  {
    icon: Send,
    label: "Step 03",
    title: "One-click order transmission",
    body:
      "Send the optimal split order to every wholesaler with a single click. No phone calls, no spreadsheets, no chasing out-of-stocks.",
    bullets: ["Order direct from PMR", "Customised order rules", "Out-of-stock chasing handled for you"],
    visual: "order",
  },
  {
    icon: BarChart3,
    label: "Step 04",
    title: "Reports & purchasing support",
    body:
      "Detailed purchasing reports delivered on your schedule. See exactly where you're saving, where margin is leaking and where opportunity lies.",
    bullets: ["Monthly savings reports", "Custom analytics on request", "Human purchasing support"],
    visual: "report",
  },
];

const integrations = [
  { icon: Database, title: "PIP code database", body: "Always up-to-date PIP & EDI maintenance handled by our team." },
  { icon: Boxes, title: "Branded & UK deals", body: "We manage your branded contracts and UK deal allocations." },
  { icon: FileCheck2, title: "Agency & DTP lines", body: "Specialist handling of agency and direct-to-pharmacy lines." },
  { icon: PhoneCall, title: "Out-of-stock chasing", body: "Stop chasing OOS - we do it so your team can focus on patients." },
];


export default function Services() {
  return (
    <div className="bg-white">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-radial-emerald" />
        <div className="absolute inset-0 bg-grid opacity-50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="grid lg:grid-cols-12 gap-8 items-end"
          >
            <div className="lg:col-span-8">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-4">How it works</div>
              <h1 data-testid={SERVICES.pageTitle} className="text-[4.1rem] font-bold text-[#0A2540] tracking-tight leading-[1.04] mb-6">
                A purchasing engine, working quietly in the background.
              </h1>
              <p className="text-lg lg:text-xl text-slate-600 leading-relaxed max-w-3xl">
                No new dashboards to learn. No staff retraining. Cascade Plus plugs into your existing PMR and starts paying for itself from your very next order.
              </p>
            </div>
            <div className="lg:col-span-4 grid grid-cols-3 gap-3">
              {["PMR", "Compare", "Transmit"].map((label) => (
                <div key={label} className="rounded-2xl border border-slate-200 bg-white px-4 py-4 text-center shadow-sm">
                  <div className="text-sm font-bold text-[#0A2540]">{label}</div>
                  <div className="text-[11px] uppercase tracking-wider text-slate-400 mt-1">built in</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* STEPS - VERTICAL TIMELINE */}
      <section className="py-20 lg:py-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative">
            <div className="absolute left-7 lg:left-1/2 top-0 bottom-0 w-px bg-slate-200" />
            <div className="space-y-12 lg:space-y-20">
              {steps.map((s, i) => (
                <motion.div
                  key={s.title}
                  data-testid={SERVICES.stepCard(i + 1)}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                  className={`relative grid lg:grid-cols-2 gap-8 lg:gap-16 items-center ${i % 2 === 1 ? "lg:[direction:rtl]" : ""}`}
                >
                  <div className="absolute left-7 lg:left-1/2 -translate-x-1/2 h-14 w-14 rounded-2xl bg-[#0A2540] flex items-center justify-center shadow-lg z-10">
                    <s.icon className="h-6 w-6 text-emerald-400" />
                  </div>
                  <div className={`pl-24 lg:pl-0 [direction:ltr] ${i % 2 === 1 ? "lg:text-right" : ""}`}>
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-3">{s.label}</div>
                    <h2 className="text-3xl lg:text-4xl font-bold text-[#0A2540] mb-4">{s.title}</h2>
                    <p className="text-lg text-slate-600 leading-relaxed mb-6">{s.body}</p>
                    <ul className={`space-y-2 ${i % 2 === 1 ? "lg:[&_li]:flex-row-reverse" : ""}`}>
                      {s.bullets.map((b) => (
                        <li key={b} className="flex items-center gap-3 text-slate-700 font-medium">
                          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                          {b}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="pl-24 lg:pl-0 [direction:ltr]">
                    <WorkflowVisual type={s.visual} />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* INTEGRATIONS */}
      <section className="py-20 bg-slate-50/60 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-3">Built-in</div>
            <h2 className="text-4xl font-bold text-[#0A2540] mb-4">Everything else, handled.</h2>
            <p className="text-lg text-slate-600">Beyond core ordering, Cascade Plus takes care of the operational fiddly bits that eat your time.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {integrations.map((it) => (
              <div key={it.title} className="bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-md transition-all">
                <div className="h-11 w-11 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-4">
                  <it.icon className="h-5 w-5" />
                </div>
                <h3 className="text-lg font-bold text-[#0A2540] mb-2">{it.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{it.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-[#0A2540] mb-4">See it on your own numbers.</h2>
          <p className="text-lg text-slate-600 mb-3">Share your current spend - we'll show you the savings, line by line.</p>
          <p className="text-xs text-slate-500 mb-8">Illustrative savings depend on item mix, supplier availability, existing buying terms, and selected order rules.</p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-[#0A2540] text-white px-7 py-4 rounded-full font-semibold hover:bg-[#0F365C] transition-all hover:gap-3 shadow-lg"
          >
            Request a comparison
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}

function WorkflowVisual({ type }) {
  if (type === "connect") {
    return (
      <div className="aspect-[4/3] rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between border-b border-slate-100 pb-4 mb-5">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">PMR connection</div>
            <div className="text-lg font-bold text-[#0A2540]">Branch order feed</div>
          </div>
          <Plug className="h-8 w-8 text-emerald-500" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          {["ProScript Connect", "Positive Solutions", "RxWeb", "Apotec"].map((name) => (
            <div key={name} className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
              <div className="text-sm font-semibold text-[#0A2540]">{name}</div>
              <div className="text-xs text-emerald-600 mt-1">ready</div>
            </div>
          ))}
        </div>
        <div className="mt-5 rounded-xl bg-emerald-50 border border-emerald-100 px-4 py-3 text-sm font-semibold text-emerald-700">
          Setup checklist completed by Cascade Plus
        </div>
      </div>
    );
  }

  if (type === "compare") {
    return (
      <div className="aspect-[4/3] rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-5">
          <ShoppingBasket className="h-8 w-8 text-emerald-500" />
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Live basket</div>
            <div className="text-lg font-bold text-[#0A2540]">12 lines compared</div>
          </div>
        </div>
        {[
          ["Atorvastatin 20mg", "Alliance Healthcare", "\u00a318.42"],
          ["Omeprazole 20mg", "Alliance Healthcare", "\u00a39.16"],
          ["Amlodipine 5mg", "Phoenix", "\u00a33.84"],
        ].map(([drug, supplier, price]) => (
          <div key={drug} className="mb-3 grid grid-cols-[1fr_auto] gap-3 rounded-xl border border-slate-200 p-3">
            <div>
              <div className="text-sm font-semibold text-[#0A2540]">{drug}</div>
              <div className="text-xs text-slate-500">best matched supplier: {supplier}</div>
            </div>
            <div className="text-sm font-bold text-emerald-600">{price}</div>
          </div>
        ))}
      </div>
    );
  }

  if (type === "order") {
    return (
      <div className="aspect-[4/3] rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Order split</div>
            <div className="text-lg font-bold text-[#0A2540]">Ready to transmit</div>
          </div>
          <Send className="h-8 w-8 text-emerald-500" />
        </div>
        {[
          ["AAH Pharmaceuticals", "4 lines", "\u00a3142.20"],
          ["Lexon", "5 lines", "\u00a388.40"],
          ["Ethigen", "3 lines", "\u00a361.75"],
        ].map(([supplier, lines, value]) => (
          <div key={supplier} className="mb-3 flex items-center justify-between rounded-xl bg-slate-50 border border-slate-200 px-4 py-3">
            <div>
              <div className="font-bold text-[#0A2540]">{supplier}</div>
              <div className="text-xs text-slate-500">{lines}</div>
            </div>
            <div className="text-sm font-semibold text-slate-700">{value}</div>
          </div>
        ))}
        <div className="mt-4 rounded-xl bg-[#0A2540] px-4 py-3 text-center text-sm font-bold text-white">
          One-click transmission
        </div>
      </div>
    );
  }

  return (
    <div className="aspect-[4/3] rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex items-center gap-3 mb-5">
        <PackageCheck className="h-8 w-8 text-emerald-500" />
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Monthly report</div>
          <div className="text-lg font-bold text-[#0A2540]">Spend by Supplier</div>
        </div>
      </div>
      <div className="overflow-hidden rounded-xl border border-slate-200">
        <div className="grid grid-cols-[1fr_0.8fr_0.8fr] bg-slate-50 px-4 py-3 text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500">
          <div>Supplier</div>
          <div className="text-right">Spend</div>
          <div className="text-right">% of total</div>
        </div>
        {[
          ["AAH", "\u00a3450.00", "38.97%", "bg-violet-500"],
          ["Alliance", "\u00a3380.00", "32.91%", "bg-blue-500"],
          ["Phoenix", "\u00a3174.60", "15.12%", "bg-teal-500"],
          ["B&S Distribution", "\u00a390.00", "7.79%", "bg-indigo-300"],
          ["Ethigen", "\u00a360.00", "5.20%", "bg-green-500"],
        ].map(([supplier, spend, share, color]) => (
          <div key={supplier} className="grid grid-cols-[1fr_0.8fr_0.8fr] items-center border-t border-slate-100 px-4 py-2 text-sm">
            <div className="flex items-center gap-2 font-semibold text-[#0A2540]">
              <span className={`h-2.5 w-2.5 rounded-full ${color}`} />
              {supplier}
            </div>
            <div className="text-right font-semibold text-[#0A2540]">{spend}</div>
            <div className="text-right font-semibold text-[#0A2540]">{share}</div>
          </div>
        ))}
        <div className="grid grid-cols-[1fr_0.8fr_0.8fr] border-t border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold text-[#0A2540]">
          <div>Total</div>
          <div className="text-right">&pound;1,154.60</div>
          <div className="text-right">100%</div>
        </div>
      </div>
    </div>
  );
}
